import {
  ClientChatTypes,
  getChatTabCount,
  getTypeOfUser,
  HelpTypes,
  HelpTypesClientChat,
  setChatTabUnreadCount,
  TypeOfUser,
} from '@pharmaplan/common';
import { IChatTabUnreadCount } from '@pharmaplan/common/models/ChatModels';
import { useRef } from 'react';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';

const { pharmacist, pharmacy, admin, superAdmin, technician, student } = TypeOfUser;
const {
  users,
  support,
  pharmacist: pharmacistChat,
  pharmacy: pharmacyChat,
  technician: technicianChat,
  student: studentChat,
} = ClientChatTypes;

const useChatSidebarUnread = () => {
  const dispatch = useAppDispatch();

  const userType = useAppSelector(getTypeOfUser);
  const unreadTabCount = useAppSelector(getChatTabCount);
  const existingTabCount = useRef<IChatTabUnreadCount>({});
  existingTabCount.current = unreadTabCount;

  const professionalUser = (chatType: ClientChatTypes, url: string, unreadMessageCount: number) =>
    (
      {
        [chatType]: {
          ...existingTabCount.current[chatType],
          [url]: unreadMessageCount,
        },
      }
    );

  const handleDoubleUserTypes = (
    url: string,
    unreadMessageCount: number,
    secondType: HelpTypes,
    // eslint-disable-next-line consistent-return
  ) => {
    switch (userType) {
      case admin:
      case superAdmin:
        return {
          [users]: {
            ...existingTabCount.current[users],
            [url]: unreadMessageCount,
          },
        };
      case pharmacy:
        if (secondType === HelpTypes.pharmacist) return professionalUser(pharmacistChat, url, unreadMessageCount);
        if (secondType === HelpTypes.technicians) return professionalUser(technicianChat, url, unreadMessageCount);
        if (secondType === HelpTypes.students) return professionalUser(studentChat, url, unreadMessageCount);
        break;
      case pharmacist:
      case technician:
      case student:
        return {
          [pharmacyChat]: {
            ...existingTabCount.current[pharmacyChat],
            [url]: unreadMessageCount,
          },
        };
      default:
        return {};
    }
  };

  const handleSingleUserType = (
    url: string,
    unreadMessageCount: number,
    firstUserType: HelpTypes,
    // eslint-disable-next-line consistent-return
  ) => {
    const chatType = HelpTypesClientChat[firstUserType as keyof typeof HelpTypesClientChat] as ClientChatTypes;
    switch (userType) {
      case admin:
      case superAdmin:
        return {
          [chatType]: {
            ...existingTabCount.current[
              chatType
            ],
            [url]: unreadMessageCount,
          },
        };
      case pharmacy:
      case pharmacist:
      case technician:
      case student:
        return {
          [support]: {
            ...existingTabCount.current[support],
            [url]: unreadMessageCount,
          },
        };
      default:
        break;
    }
  };

  const handleUserType = (
    url: string,
    userTypes: string,
    unreadMessageCount: number,
  ) => {
    const splitUserType = userTypes?.split?.(',') as HelpTypes[];
    const { length } = splitUserType ?? {};
    let tabBasedCount: unknown = {};
    if (length === 2) {
      tabBasedCount = handleDoubleUserTypes(
        url,
        unreadMessageCount,
        splitUserType?.[1],
      );
    }
    if (length === 1) {
      tabBasedCount = handleSingleUserType(
        url,
        unreadMessageCount,
        splitUserType?.[0],
      );
    }

    dispatch(
      setChatTabUnreadCount({
        ...existingTabCount.current,
        ...(tabBasedCount as IChatTabUnreadCount),
      }),
    );
  };

  return { handleUserType };
};

export default useChatSidebarUnread;
